import { Injectable } from "@angular/core";
// biome-ignore lint/style/useImportType: injected as a constructor param; Angular DI needs the real class reference at runtime
import { AlertService } from "@ui";
import { BehaviorSubject, combineLatest, type Observable, type Subscription } from "rxjs";
import { map } from "rxjs/operators";
import type { Corpus } from "@pienso/ingest-types/corpus.interface";
import type { ColumnTypes } from "./data-grid.component";
// biome-ignore lint/style/useImportType: injected as a constructor param; Angular DI needs the real class reference at runtime
import { DefaultGridDataProvider } from "./default-grid-data.provider";
import type { GridDataProvider, Page } from "./grid-data.provider";
import type { GridData, GridDataService } from "./grid-data-service.interface";

/**
 * Responsible for the data the DataGridComponent sees.
 * Data goes into the grid by watching the page$ of data.
 * Trigger a page load by calling loadPage()
 */
@Injectable({ providedIn: "root" })
export class DefaultGridDataService implements GridDataService {
  public pageSize = 20;

  // protected _columnMeta$ = new BehaviorSubject<ColumnMeta>(null);
  protected _page$ = new BehaviorSubject<Page | null>(null);
  protected _columnTypes$ = new BehaviorSubject<ColumnTypes | null>(null);
  protected _gridData$ = new BehaviorSubject<GridData | null>(null);
  protected _isLoading$ = new BehaviorSubject<boolean>(false);

  protected _corpus: Corpus | undefined;
  protected _esIndex: string | null = null;

  protected provider: GridDataProvider | undefined;
  protected subscriptions: Subscription[] = [];

  constructor(
    protected defaultProvider: DefaultGridDataProvider,
    protected alertService: AlertService,
  ) {
    this.setDataProvider(defaultProvider);

    combineLatest([this._columnTypes$, this._page$])
      .pipe(
        map((combined: [ColumnTypes | null, Page | null]) => {
          const isValid = this._corpus && !combined.some((item) => !item);
          if (!isValid) {
            return null;
          }
          return <GridData>{
            columnInfo: {
              columnMeta: this._corpus!.columnMeta,
              columnTypes: combined[0],
            },
            page: combined[1],
          };
        }),
      )
      .subscribe({
        // Skip nulls: keep the last-known grid data on screen (via isLoading$) instead of
        // blanking the grid every time a reload starts.
        next: (data) => {
          if (data) {
            this._gridData$.next(data);
            this._isLoading$.next(false);
          }
        },
        error: (e) => {
          this._isLoading$.next(false);
          this.alertService.error(e);
        },
      });
  }

  public setCorpus(c: Corpus) {
    this._corpus = c;
    this._esIndex = null;
    this._isLoading$.next(true);
    this._columnTypes$.next(null);
    this._page$.next(null);
    if (c) {
      this._esIndex = c.esIndex;
      this.loadColumnTypes();
      this.loadPage(0);
    }
  }
  public get corpus() {
    return this._corpus;
  }

  // public get columnMeta$(): Observable<ColumnMeta> { return this._columnMeta$.asObservable(); }
  // get columnMeta() { return this._columnMeta$.getValue(); }

  /** Use to feed the data grid with data */
  public get gridData$(): Observable<GridData | null> {
    return this._gridData$.asObservable();
  }
  // public get page$(): Observable<Page> { return this._page$.asObservable(); }

  /** True while a corpus/page/column-type reload is in flight; gridData$ keeps its last value until it resolves. */
  public get isLoading$(): Observable<boolean> {
    return this._isLoading$.asObservable();
  }

  /** Request a page of data be loaded and sent to page$ */
  loadPage(pageNumber: number) {
    // The grid's paginator emits an initial page event on mount, which can precede setCorpus().
    if (!this._esIndex) {
      return;
    }
    this.provider?.loadPage(this._esIndex, this.pageSize, pageNumber);
  }

  /** Change the page size and reload from the first page */
  setPageSize(pageSize: number) {
    this.pageSize = pageSize;
    this.loadPage(0);
  }

  // public get columnTypes$(): Observable<ColumnTypes> {
  //   return this._columnTypes$.asObservable();
  // }
  public get columnTypes() {
    return this._columnTypes$.getValue();
  }

  public loadColumnTypes() {
    if (!this._esIndex) {
      return;
    }
    this.provider?.loadColumnTypes(this._esIndex);
  }

  public reload() {
    this._isLoading$.next(true);
    this._columnTypes$.next(null);
    this.loadColumnTypes();
    this.loadPage(0);
  }

  /** Overrice the default provider when another functionality is desired */
  public setDataProvider(provider: GridDataProvider | null) {
    const unchanged = this.provider === provider || (provider === null && this.provider === this.defaultProvider);
    if (!unchanged) {
      if (this.subscriptions.length) {
        this.subscriptions.forEach((s) => s.unsubscribe());
        this.subscriptions = [];
      }
      this.provider = provider ? provider : this.defaultProvider;
      this.subscriptions.push(
        this.provider.page$.subscribe({
          next: (page) => this._page$.next(page),
          error: (e) => {
            this._isLoading$.next(false);
            this.alertService.error(e);
          },
        }),
        this.provider.columnTypes$.subscribe({
          next: (cols) => this._columnTypes$.next(cols),
          error: (e) => {
            this._isLoading$.next(false);
            this.alertService.error(e);
          },
        }),
      );
    }
    if (this._corpus) {
      this.reload();
      // this.loadPage(0);
    }
  }
}
